import './PriceDistributionChart.scss';

import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart as ChartJS } from 'chart.js/auto';
import { Chart } from 'primereact/chart';
import { DeviceCurrentStateService } from '../../service/DeviceCurrentStateService';
import { DeviceService } from '../../service/DeviceService';
import { UserService } from '../../service/UserService';
import { PlanogramService } from '../../service/PlanogramService';
import { FormattedUtils } from '../../utils/FormattedUtils';
import Dropdown from '../../component/input/Dropdown';
import LoadingSpinner from '../../component/loadingoverlay/LoadingSpinner';
import { MessageContext } from '../../context/MessageContext';
import { useContext } from 'react';
import { t } from 'i18next';
import { Dialog } from 'primereact/dialog';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import DataTable, { FILTER_TYPES } from '../../component/datatable/DataTable';
import { Column } from 'primereact/column';
import { PriceDistributionDialogDataTableContext, PriceDistributionDialogDataTableProvider } from '../../component/datatable/DataTableContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';


// Plugin to draw center text inside doughnut
const priceDistributionCenterTextPlugin = {
    id: 'priceDistributionCenterText',
    afterDatasetsDraw(chart, args, pluginOptions) {
        if (chart.config.type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const title = t('priceDistributionChart.averagePrice')?.toUpperCase();
        const averagePrice = pluginOptions?.averagePrice || 0;
        const currency = pluginOptions?.currency || '';
        const subtitle = FormattedUtils.formatPrice(averagePrice, currency);
        const titleColor = '#736f89';
        const subtitleColor = '#1e1531';

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Title (small)
        ctx.fillStyle = titleColor;
        ctx.font = '500 12px Inter-Medium, sans-serif';
        ctx.fillText(title, centerX, centerY - 14);

        // Subtitle (big)
        ctx.fillStyle = subtitleColor;
        ctx.font = '700 22px Inter-Bold, sans-serif';
        ctx.fillText(subtitle, centerX, centerY + 10);

        ctx.restore();
    }
};

// Gradient definitions for chart segments (shared between plugin and component)
const gradientDefinitions = [
    {
        angle: 120.0,
        startColor: 'rgba(59, 130, 246, 1)',
        endColor: 'rgba(147, 197, 253, 1)'
    },
    {
        angle: 178.12,
        startColor: 'rgba(255, 132, 19, 1)',
        endColor: 'rgba(255, 199, 114, 1)'
    },
    {
        angle: 45.0,
        startColor: 'rgba(34, 197, 94, 1)',
        endColor: 'rgba(134, 239, 172, 1)'
    },
    {
        angle: 155.79,
        startColor: 'rgba(126, 105, 231, 1)',
        endColor: 'rgba(165, 148, 250, 1)'
    },    
    {
        angle: -22.86,
        startColor: 'rgba(255, 67, 38, 1)',
        endColor: 'rgba(250, 184, 174, 1)'
    },    
    {
        angle: -6.97,
        startColor: 'rgba(193, 27, 198, 1)',
        endColor: 'rgba(229, 158, 232, 1)'
    },
    {
        angle: 10.07,
        startColor: 'rgba(47, 189, 255, 1)',
        endColor: 'rgba(197, 236, 254, 1)'
    },
    {
        angle: -22.86,
        startColor: 'rgba(255, 67, 38, 1)',
        endColor: 'rgba(250, 184, 174, 1)'
    },    
    
];

// Plugin to fill white background on canvas
const whiteBackgroundPlugin = {
    id: 'whiteBackground',
    beforeDraw(chart, args, pluginOptions) {
        const { ctx } = chart;
        const canvas = ctx && ctx.canvas;
        if (!canvas) return;
        ctx.save();
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    }
};

// Plugin to draw doughnut segments with gradients
const priceDistributionGradientsPlugin = {
    id: 'priceDistributionGradients',
    afterDatasetsDraw(chart, args, pluginOptions) {
        if (chart.config.type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const dataset = chart.data.datasets[0];
        const data = Array.isArray(dataset?.data) ? dataset.data : [];
        if (!data.length) return;

        const gradients = pluginOptions?.gradients || chart.options?.segmentGradients || [];
        if (!gradients.length) return;

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const outerRadius = Math.min(chartArea.width, chartArea.height) / 2;
        const innerRadius = outerRadius * 0.68; // 68% cutout

        // Calculate total for segment angles
        const total = data.reduce((sum, val) => sum + val, 0);
        if (total === 0) return;

        let currentAngle = -Math.PI / 2; // Start at top

        // Draw each segment with gradient AFTER Chart.js renders its segments
        // Direct mapping: first gradient for first value (highest price), second for second, etc.
        data.forEach((value, index) => {
            const segmentAngle = (value / total) * 2 * Math.PI;
            const gradientDef = gradients[index];
            
            if (!gradientDef) {
                return;
            }

            // Skip if segment angle is too small
            if (segmentAngle < 0.001) {
                return;
            }

            // Create gradient for this segment
            const angleRad = (gradientDef.angle * Math.PI) / 180;
            const x1 = centerX + Math.cos(angleRad) * outerRadius * 0.5;
            const y1 = centerY + Math.sin(angleRad) * outerRadius * 0.5;
            const x2 = centerX - Math.cos(angleRad) * outerRadius * 0.5;
            const y2 = centerY - Math.sin(angleRad) * outerRadius * 0.5;

            const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
            gradient.addColorStop(0, gradientDef.startColor);
            gradient.addColorStop(1, gradientDef.endColor);

            // Draw the segment
            ctx.save();
            ctx.beginPath();
            // Outer arc
            ctx.arc(centerX, centerY, outerRadius, currentAngle, currentAngle + segmentAngle);
            // Inner arc (reverse direction)  
            ctx.arc(centerX, centerY, innerRadius, currentAngle + segmentAngle, currentAngle, true);
            ctx.closePath();
            ctx.fillStyle = gradient;
            ctx.fill();
            ctx.restore();

            currentAngle += segmentAngle;
        });
    }
};

// Register plugins globally
// Order matters: gradients should be drawn after Chart.js renders, but before center text and labels



const PriceDistributionChart = ({ productId, currency = '' }) => {
    const { t } = useTranslation();
    const { showErrorMessage } = useContext(MessageContext);
    const isMounted = useRef(true);

    const [priceDistributionData, setPriceDistributionData] = useState(null);
    const [maxPrices, setMaxPrices] = useState(8);
    const [isLoadingPriceDistribution, setIsLoadingPriceDistribution] = useState(false);
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [dialogState, setDialogState] = useState({ visible: false, price: null });
    
    useEffect(() => {
        isMounted.current = true;
        ChartJS.register(whiteBackgroundPlugin);
        ChartJS.register(priceDistributionGradientsPlugin);
        ChartJS.register(priceDistributionCenterTextPlugin);
        return () => {
            isMounted.current = false;
            ChartJS.unregister(whiteBackgroundPlugin);
            ChartJS.unregister(priceDistributionGradientsPlugin);
            ChartJS.unregister(priceDistributionCenterTextPlugin);
        };
    }, []);

    useEffect(() => {
        if (productId && maxPrices > 0) {
            fetchPriceDistribution();
        }
    }, [productId, maxPrices]);

    useEffect(() => {
        if (priceDistributionData && priceDistributionData.prices && priceDistributionData.prices.length > 0) {
            updateChartData();
        }
    }, [priceDistributionData, currency]);

    const fetchPriceDistribution = async () => {
        try {
            setIsLoadingPriceDistribution(true);
            const data = await DeviceCurrentStateService.getPriceDistribution(productId, maxPrices);
            if (isMounted.current) {
                setPriceDistributionData(data || { prices: [], averagePrice: 0 });
            }
        } catch (error) {
            if (isMounted.current) {
                showErrorMessage(t('priceDistributionChart.error.fetch'));
            }
        } finally {
            if (isMounted.current) {
                setIsLoadingPriceDistribution(false);
            }
        }
    };
    


    const updateChartData = () => {
        if (!priceDistributionData || !priceDistributionData.prices || priceDistributionData.prices.length === 0) {
            setChartData({ labels: [], datasets: [{ data: [] }] });
            return;
        }

        const prices = priceDistributionData.prices;
        
        // Sort by price descending (highest first)
        // First value (index 0) = highest price, second value (index 1) = second highest, etc.
        const sortedData = [...prices].sort((a, b) => b.price - a.price);
        
        const labels = sortedData.map(item => `${FormattedUtils.formatPrice(item.price, currency)}`);
        const values = sortedData.map(item => item.deviceCount);
        
        // Use averagePrice from API response
        const avgPrice = priceDistributionData.averagePrice || 0;

        // Assign gradient colors in order: first gradient for first value (highest price),
        // second gradient for second value (second highest price), etc.
        // Maximum 8 gradients available (matching max 8 prices)
        const segmentGradients = sortedData.map((_, index) => {
            return gradientDefinitions[index]; // Direct mapping - no modulo needed since max 8 prices
        });
        
        // Disable Chart.js default segment rendering - plugin will draw all segments with gradients
        // Use transparent colors so Chart.js calculates positions but doesn't render visible segments
        const backgroundColors = segmentGradients.map(() => 'rgba(0, 0, 0, 0)');

        setChartData({
            labels,
            datasets: [{
                data: values,
                backgroundColor: backgroundColors,
                borderWidth: 0,
                hoverOffset: 0,
                borderRadius: 0,
                // Plugin will draw segments with gradients
                hidden: false
            }]
        });

        setChartOptions({
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            cutout: '68%',
            layout: {
                padding: {
                    top: 40
                }
            },
            plugins: {
                whiteBackground: {},
                legend: {
                    display: false
                },
                priceDistributionGradients: {
                    gradients: segmentGradients
                },
                priceDistributionCenterText: {
                    averagePrice: avgPrice,
                    currency: currency
                },
                tooltip: {
                    enabled: false
                }
            },
            segmentGradients: segmentGradients,
            // Disable animations to prevent rendering issues
            animation: false,
            // Ensure all elements are drawn
            elements: {
                arc: {
                    borderWidth: 0
                }
            }
        });
    };

    return (
        <div className="price-distribution-section">
            <PriceDistributionDialog visible={dialogState.visible} onHide={() => setDialogState({ visible: false, price: null })} price={dialogState.price} productId={productId}/>
            <div className="price-distribution-header">
                <span className="price-distribution-title">{t('priceDistributionChart.title') || 'Price distribution'}</span>
                <Dropdown
                    className="price-distribution-dropdown"
                    showLabel={false}
                    small={true}
                    id="maxPrices"
                    options={[4, 5, 6, 7, 8].map(num => ({ label: t(`priceDistributionChart.maxOptions.${num}`), value: num }))}
                    value={maxPrices}
                    onChange={(e) => setMaxPrices(e.value)}
                    optionLabel="label"
                    optionValue="value"
                />
            </div>
            {isLoadingPriceDistribution ? (
                <div className="price-distribution-chart-loading">
                    <LoadingSpinner />
                </div>
            ) : priceDistributionData && priceDistributionData.prices && priceDistributionData.prices.length > 0 ? (
                <div className="price-distribution-chart-container">
                    <div className="price-distribution-chart-wrapper">
                        <Chart
                            type="doughnut"
                            data={chartData}
                            options={chartOptions}
                        />
                    </div>
                    <div className="price-distribution-legend">
                        {priceDistributionData.prices
                            .sort((a, b) => b.price - a.price)
                            .map((item, index) => {
                                const gradientDef = gradientDefinitions[index];
                                const dotColor = gradientDef?.startColor || gradientDefinitions[0].startColor;
                                return (
                                    <div key={index} className="price-distribution-legend-item">
                                        <div 
                                            className="price-distribution-legend-dot" 
                                            style={{ backgroundColor: dotColor }}
                                        />
                                        <span className="price-distribution-legend-text cursor-pointer" onClick={() => {
                                            setDialogState({ visible: true, price: item.price });
                                        }}>
                                            {FormattedUtils.formatPrice(item.price, currency)} - {t('priceDistributionChart.device', {count: item.deviceCount})}
                                        </span>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            ) : (
                <div className="price-distribution-chart-empty">
                    {t('priceDistributionChart.noPriceData')}
                </div>
            )}
        </div>
    );

    
};

const PriceDistributionDialog = ({ visible, onHide, price, productId }) => {
    return (
        <PriceDistributionDialogDataTableProvider>
            <PriceDistributionDialogImpl visible={visible} onHide={onHide} price={price} productId={productId} />
        </PriceDistributionDialogDataTableProvider>
    )
}
const PriceDistributionDialogImpl = ({ visible, onHide, price, productId }) => {
    const { searchCriteria, setSearchCriteria } = useContext(PriceDistributionDialogDataTableContext);
    const [technicians, setTechnicians] = useState([]);
    const [planograms, setPlanograms] = useState([]);
    const tableRef = useRef(null);
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);
    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 10,
        sortField: 'name',
        sortOrder: 1,
        productId: productId,
        price: price,
        status: 'ACTIVE',
    }), [productId, price]);

    useEffect(() => {
        fetchTechnicians();
        fetchPlanograms();
    }, []);

    useEffect(() => {
        if (visible) {
            setSearchCriteria({...defaultSearchCriteria});
        } else {
            setSearchCriteria(null);
        }
    }, [visible]);

    const filterConfig = useMemo(() => [
        {
            fieldName: 'systemId',
            label: t('priceDistributionChart.dialog.deviceId'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'systemId'
        },
        {
            fieldName: 'name',
            label: t('priceDistributionChart.dialog.deviceName'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'name'
        },
        {
            fieldName: 'location.name',
            label: t('priceDistributionChart.dialog.locationName'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'location_name'
        },
        {
            fieldName: 'technicians',
            label: t('priceDistributionChart.dialog.technicians'),
            type: FILTER_TYPES.LIST,
            params: technicians,
            apiParamName: 'technicians',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'planogram.name',
            label: t('priceDistributionChart.dialog.planogramName'),
            type: FILTER_TYPES.LIST,
            params: planograms,
            apiParamName: 'planogram_ids',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
    ], [technicians, planograms]);

    const fetchTechnicians = () => {
        UserService.getTechniciansList()
            .then(technicians => {
                setTechnicians(technicians.map(r => ({ id: r.id, label: r.name })))
            })
            .catch(err => {
                showErrorMessage(t('priceDistributionChart.error.fetchTechnicians'))
            })
    }

    const fetchPlanograms = () => {
        PlanogramService.getPlanogramNamesForCurrentMerchantNotDeleted()
            .then(planograms => {
                setPlanograms(planograms.map(r => ({ id: r.id, label: r.name })))
            })
            .catch(err => {
                showErrorMessage(t('priceDistributionChart.error.fetchPlanograms'))
            })
    }

    const fetchDevices = async (criteria) => {
        try {
            return await DeviceService.getList(criteria);            
        } catch (error) {
            showErrorMessage(t('priceDistributionChart.error.fetchDevices'))
        }
    }

    return (
        <Dialog header={t('priceDistributionChart.dialog.header', {price: price})} visible={visible} onHide={onHide}
            closeIcon={<CloseIcon /> }
            style={{maxHeight: '800px'}}
        >
            <div className="widget">
                <DataTable fetchData={fetchDevices} filterConfig={filterConfig} dataTableContext={PriceDistributionDialogDataTableContext} globalSearchPosition='none' ref={tableRef}>
                    <Column field='systemId' header={t('priceDistributionChart.dialog.deviceId')} sortable sortField='systemId' id='systemId' />
                    <Column field='name' header={t('priceDistributionChart.dialog.deviceName')} sortable sortField='name' id='name' style={{ minWidth: '120px' }}/>
                    <Column field='location.name' header={t('priceDistributionChart.dialog.locationName')} sortable sortField='location.name' id='locationName' style={{ maxWidth: '150px' }}/>
                    <Column field='technicians' header={t('priceDistributionChart.dialog.technicians')} sortable sortField='technicians' id='technicians'
                        body={row => RenderedHelper.renderFirstWithMore(row.technicians, t('priceDistributionChart.dialog.technicians'))} style={{ minWidth: '120px', maxWidth: '160px' }}
                    />
                    <Column field='planogram.name' header={t('priceDistributionChart.dialog.planogramName')} sortable sortField='planogram.name' id='planogramName' />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default PriceDistributionChart;

